import { createStyles, Grid, makeStyles, Theme } from '@material-ui/core';

import { Tip as InvoiceTip, InvoiceTipDistribution, ManualTipInput } from '@spike/invoice-model';
import { Option } from '@spike/model';
import clsx from 'clsx';
import { OptionsField } from 'components/UI';
import round from 'lodash/round';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { reduceResolution, wbp } from 'Theme';
import Section from '../Section';
import Tip from './Tip';
import TipDistribution from './TipDistribution';
import { CheckoutTip, TipOption } from './TipModel';
import {
    convertToCheckoutTip,
    convertToInvoiceTip,
    isCustomCheckoutTip,
    isDefaultCheckoutTip,
    isNoCheckoutTip,
    isSameTip
} from './TipUtils';
import CustomTip from './CustomTip';

interface TipsProps {
    invoiceId: number;
    invoiceTip: InvoiceTip | null;
    tipDistribution: Array<InvoiceTipDistribution>;
    baseStaffIds: ReadonlySet<number>;
    subtotalWithoutTipTaxes: string;
    saving: boolean;
    tipsManual?: boolean;
    disabled?: boolean;
    className?: string;
    totalBalance?: (value: string) => void;
    onChange: (tip: InvoiceTip | null, tipsToDestroy?: Array<ManualTipInput>, force?: boolean) => void;
    onTipDistributionValidChange?: (valid: boolean) => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        cell: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%'
        },
        optionsContainer: {
            [theme.breakpoints.down(wbp)]: {
                marginTop: `${reduceResolution(20)}px`,
                height: `${reduceResolution(32)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                marginTop: '20px',
                height: '32px'
            }
        },
        onlyOptions: {
            marginTop: '0px'
        },
        customContainer: {
            [theme.breakpoints.down(wbp)]: {
                marginTop: `${reduceResolution(29)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                marginTop: '29px'
            }
        },
        loading: {
            opacity: 0.2
        }
    })
);

const customOption: Option<string> = { id: 'custom', name: 'Custom Amount' };
const noTipOption: Option<string> = { id: 'no_tip', name: 'No Tip' };

const options: Array<Option<string>> = [customOption, noTipOption];

const zeroAmountDefaultTips: Array<TipOption> = [
    {
        uuid: '5',
        amount: '5',
        percentage: null
    },
    {
        uuid: '10',
        amount: '10',
        percentage: null
    },
    {
        uuid: '15',
        amount: '15',
        percentage: null
    }
];

export const Tips: FunctionComponent<TipsProps> = props => {
    const classes = useStyles();

    const isZeroAmount = Number(props.subtotalWithoutTipTaxes) === 0;

    const amountDefaultTips = useSelector<RootState, Array<TipOption>>(state =>
        state.marketplace.marketplace.tips
            .filter(tip => tip.percentage > 0)
            .map(tip => ({
                uuid: tip.uuid,
                amount: round((Number(props.subtotalWithoutTipTaxes) * tip.percentage) / 100, 2)
                    .toFixed(2)
                    .toString(),
                percentage: String(tip.percentage)
            }))
    );

    const defaultTips = isZeroAmount ? zeroAmountDefaultTips : amountDefaultTips;
    const [saving, setSaving] = useState(props.saving);
    const [tipResetTrigger, setTipResetTrigger] = useState(0);
    const [hasAddedRows, setHasAddedRows] = useState(false);
    const [redistributeKey, setRedistributeKey] = useState(0);
    const [redistributeAmount, setRedistributeAmount] = useState('0.00');
    const [clearAddedRowsTrigger, setClearAddedRowsTrigger] = useState(0);
    const [distributionHasStaff, setDistributionHasStaff] = useState(() =>
        props.tipDistribution.some(d => !!d.staff?.id)
    );

    const deriveSelectedOption = (tip: CheckoutTip): Option<string> | undefined => {
        if (isDefaultCheckoutTip(tip)) return undefined;
        if (isNoCheckoutTip(tip)) return noTipOption;
        if (isCustomCheckoutTip(tip)) return customOption;
        return undefined;
    };

    const [checkoutTip, setCheckoutTip] = useState<CheckoutTip>(() =>
        convertToCheckoutTip(props.invoiceTip, defaultTips)
    );
    const [selectedOption, setSelectedOption] = useState<Option<string> | undefined>(() =>
        deriveSelectedOption(convertToCheckoutTip(props.invoiceTip, defaultTips))
    );

    const prevInvoiceTipRef = useRef(props.invoiceTip);
    const pendingUserChangeRef = useRef(false);
    const suppressNextNoTipSyncRef = useRef(false);

    useEffect(() => {
        if (props.invoiceTip === prevInvoiceTipRef.current) return;
        prevInvoiceTipRef.current = props.invoiceTip;

        if (pendingUserChangeRef.current) {
            pendingUserChangeRef.current = false;
            return;
        }

        const next = convertToCheckoutTip(props.invoiceTip, defaultTips);

        // Backend clears the tip when the last distribution row is deleted.
        // While the user is on the custom-amount radio, keep that selection at $0 so the
        // "add a staff member" warning and disabled Pay button persist (this covers the
        // all-added case where deleting every row also leaves distributionHasStaff false).
        if (isNoCheckoutTip(next) && (suppressNextNoTipSyncRef.current || selectedOption?.id === customOption.id)) {
            suppressNextNoTipSyncRef.current = false;
            setCheckoutTip({ default: null, customTip: { amount: '0.00', percentage: null } });
            return;
        }

        setCheckoutTip(next);

        // Backend may clear invoiceTip as a side effect of saving tip distribution amounts.
        // If tip became "no tip" but distribution has staff assigned, preserve UI state.
        if (isNoCheckoutTip(next) && distributionHasStaff) {
            return;
        }

        setSelectedOption(deriveSelectedOption(next));
    }, [props.invoiceTip]);

    useEffect(() => {
        setSaving(props.saving);
    }, [props.saving]);

    // A custom $0 tip is reported as "no tip" by isCustomCheckoutTip, so also treat the
    // custom radio being selected as requiring staff — otherwise deleting every distribution
    // row would re-enable Pay and hide the warning while custom is still selected at $0.
    const tipRequiresStaff =
        isDefaultCheckoutTip(checkoutTip) || isCustomCheckoutTip(checkoutTip) || selectedOption?.id === customOption.id;

    const currentTipAmount = (): string => {
        if (checkoutTip.default?.amount != null) return Number(checkoutTip.default.amount).toFixed(2);
        if (checkoutTip.customTip != null) return computeCustomTipAmount(convertToInvoiceTip(checkoutTip));
        return '0.00';
    };

    useEffect(() => {
        const valid = !tipRequiresStaff || distributionHasStaff;
        props.onTipDistributionValidChange?.(valid);
    }, [checkoutTip, distributionHasStaff, selectedOption]);

    const buildDestroyInputs = (): Array<ManualTipInput> =>
        props.tipDistribution
            .filter(d => d.id !== undefined)
            .map(d => ({ id: d.id!, destroy: true, staffId: d.staff.id }));

    const notifyTipChange = (tip: InvoiceTip | null, tipsToDestroy?: Array<ManualTipInput>, force?: boolean) => {
        pendingUserChangeRef.current = true;
        props.onChange(tip, tipsToDestroy, force);
    };

    const computeCustomTipAmount = (customTip: InvoiceTip | null): string => {
        if (!customTip) return '0.00';
        if (customTip.amount !== null) return Number(customTip.amount).toFixed(2);
        if (customTip.percentage !== null) {
            return round((Number(props.subtotalWithoutTipTaxes) * Number(customTip.percentage)) / 100, 2).toFixed(2);
        }
        return '0.00';
    };

    const changeDefaultTipHandler = (defaultTip: TipOption) => {
        const newCheckoutTip = {
            default: { ...defaultTip },
            customTip: null
        };

        const actualInvoiceTip = convertToInvoiceTip(checkoutTip);
        const newInvoiceTip = convertToInvoiceTip(newCheckoutTip);

        setSelectedOption(undefined);
        setCheckoutTip(newCheckoutTip);

        if (!isSameTip(actualInvoiceTip, newInvoiceTip)) {
            setSaving(true);
            setClearAddedRowsTrigger(prev => prev + 1);
            notifyTipChange(newInvoiceTip, buildDestroyInputs());
        }
    };

    const changeOptionHandler = (option: Option<string>) => {
        const wasNoTip = selectedOption?.id === noTipOption.id;
        setSelectedOption(option);

        // No Tip -> Custom: seed the custom amount from the first predefined tip's amount (e.g.
        // first option 15%/$30 -> $30) and save so the backend recreates the staff distribution
        // lines. Forced through in case that amount is $0 (it would otherwise be swallowed by the
        // isSameTip guard). The user refines the amount afterwards via the custom field.
        if (option.id === customOption.id && wasNoTip) {
            const firstDefaultAmount = defaultTips[0]?.amount ?? '0';
            const recreatedTip = { default: null, customTip: { amount: firstDefaultAmount, percentage: null } };
            setCheckoutTip(recreatedTip);
            setSaving(true);
            notifyTipChange(convertToInvoiceTip(recreatedTip), undefined, true);
            return;
        }

        const inheritedAmount = option.id === customOption.id ? checkoutTip.default?.amount ?? '0' : '0';
        const newCheckoutTip = {
            default: null,
            customTip: { amount: inheritedAmount, percentage: null }
        };
        setCheckoutTip(newCheckoutTip);

        const isNoTip = option.id === noTipOption.id;
        const destroyInputs = isNoTip ? buildDestroyInputs() : undefined;
        const tipChanged = !isSameTip(convertToInvoiceTip(newCheckoutTip), convertToInvoiceTip(checkoutTip));

        // Only enter the save path when something will actually change. Forcing it on every
        // No Tip click left `saving` stuck (greying out the radios) when the tip was already
        // $0 and there were no distribution rows left to destroy.
        if (tipChanged || (destroyInputs?.length ?? 0) > 0) {
            setSaving(true);
            notifyTipChange(convertToInvoiceTip(newCheckoutTip), destroyInputs);
        }
    };

    const handleDistributionRowAdded = () => {
        if (checkoutTip.default) {
            changeOptionHandler(customOption);
        }
    };

    const handleDistributionTotalChange = (total: string) => {
        setCheckoutTip(prev => ({
            ...prev,
            customTip: { amount: total, percentage: null }
        }));
    };

    const changeCustomTipHandler = (customTip: InvoiceTip | null) => {
        setSaving(true);
        const newCheckoutTip = { default: null, customTip };
        setCheckoutTip(newCheckoutTip);

        if (props.tipsManual && hasAddedRows) {
            setRedistributeAmount(computeCustomTipAmount(customTip));
            setRedistributeKey(prev => prev + 1);
        } else if (props.tipsManual) {
            setTipResetTrigger(prev => prev + 1);
            notifyTipChange(convertToInvoiceTip(newCheckoutTip));
        } else {
            // Automatic distribution: set the new tip total and destroy the existing
            // distribution rows so the backend re-distributes from scratch (tipsManual stays
            // false). Avoids the stray per-row $0 manual save.
            notifyTipChange(convertToInvoiceTip(newCheckoutTip), buildDestroyInputs());
        }
    };

    return (
        <Section
            title="Add a tip"
            className={props.className}
        >
            <>
                {defaultTips.length > 0 && (
                    <Grid
                        container
                        spacing={2}
                    >
                        {defaultTips.map((defaultTip, index) => (
                            <Grid
                                item
                                xs={6}
                                sm={4}
                                className={classes.cell}
                                key={defaultTip.uuid}
                            >
                                <Tip
                                    id={`booking_payment_tip_${index}`}
                                    percentage={defaultTip.percentage!}
                                    amount={defaultTip.amount!}
                                    onClick={() => changeDefaultTipHandler(defaultTip)}
                                    selected={checkoutTip?.default?.uuid === defaultTip.uuid}
                                    loading={props.saving}
                                    disabled={props.disabled}
                                />
                            </Grid>
                        ))}
                    </Grid>
                )}
                <Grid
                    container
                    className={clsx(classes.optionsContainer, {
                        [classes.onlyOptions]: defaultTips.length === 0,
                        [classes.loading]: saving || (props.disabled ?? false)
                    })}
                >
                    <OptionsField
                        options={options}
                        selected={selectedOption || undefined}
                        disabled={props.disabled}
                        onChange={changeOptionHandler}
                    />
                </Grid>
                {selectedOption?.id === customOption.id && (
                    <Grid
                        container
                        className={clsx(classes.customContainer, {
                            [classes.loading]: saving || (props.disabled ?? false)
                        })}
                    >
                        <CustomTip
                            tip={checkoutTip.customTip}
                            onlyAmount={isZeroAmount}
                            saving={saving}
                            disabled={props.disabled}
                            onChange={changeCustomTipHandler}
                        />
                    </Grid>
                )}
                {selectedOption?.id !== noTipOption.id &&
                    (selectedOption?.id === customOption.id ||
                        props.tipDistribution.length > 0 ||
                        props.baseStaffIds.size === 0) && (
                        <TipDistribution
                            className={props.className}
                            invoiceId={props.invoiceId}
                            tipDistribution={props.tipDistribution}
                            baseStaffIds={props.baseStaffIds}
                            tipsManual={props.tipsManual}
                            loading={saving}
                            warning={
                                tipRequiresStaff && !distributionHasStaff
                                    ? 'Please add at least one staff member to receive the tip.'
                                    : undefined
                            }
                            initialRowAmount={currentTipAmount()}
                            tipResetTrigger={tipResetTrigger}
                            clearAddedRowsTrigger={clearAddedRowsTrigger}
                            redistributeKey={redistributeKey}
                            redistributeAmount={redistributeAmount}
                            onSaving={setSaving}
                            onTotalChange={handleDistributionTotalChange}
                            onRowAdded={handleDistributionRowAdded}
                            onHasAddedRowsChange={(hasAdded: boolean) => {
                                if (!hasAdded && hasAddedRows && selectedOption?.id === customOption.id) {
                                    suppressNextNoTipSyncRef.current = true;
                                }
                                setHasAddedRows(hasAdded);
                            }}
                            onHasAssignedStaffChange={setDistributionHasStaff}
                        />
                    )}
            </>
        </Section>
    );
};

export default Tips;
